'use client';
import { Edit02 } from '@ethsign/icons';
import { AnnotationEditorType } from '@ethsign/pdf-editor';
import { Button, ScrollArea, Tooltip } from '@ethsign/ui';
import { groupBy } from 'lodash-es';
import React from 'react';

import {
  CheckGreenIcon,
  CircleCheckedIcon,
  CircleIcon,
  DateIcon,
  DateIconActive,
  SignatureIcon,
  SignatureIconActive,
  TextIcon,
  TextIconActive
} from '@/components';

import { dragImage } from '@/constants/editor';
import { ContractItem, ContractType } from '@/types';
import { isSameUser } from '@/utils';
const fieldsInfo = {
  signature: {
    text: 'Signature',
    icons: {
      active: <SignatureIconActive />,
      normal: <SignatureIcon />
    }
  },
  date: {
    text: 'Date Signed',
    icons: {
      active: <DateIconActive />,
      normal: <DateIcon />
    }
  },
  text: {
    text: 'Text',
    icons: {
      active: <TextIconActive />,
      normal: <TextIcon />
    }
  }
};
interface Props {
  fields: any[];
  onFieldClick: (Id: string) => void;
  currentUser: any;
  onSubmit: () => void;
  contract: ContractItem;
  selectedField: string;
  dragEnd: (e: any) => void;
  onSignatureClick?: (data: any) => void;
}

export function LefPanel(props: Props) {
  const { fields, onFieldClick, currentUser, onSubmit, contract, selectedField, dragEnd } = props;
  const fieldsGroup = groupBy(fields, (item) => item.PageNumber);
  const isCompleted =
    contract?.recipients?.filter((item) => item.role === 'signer').find((item: any) => isSameUser(item, currentUser))
      ?.status === 'signed';
  const islinkSignContract = contract.contractType === ContractType.link_sign_contract;
  const disabledButton = !fields.every((item) => {
    if (item.required) {
      return item.completed;
    } else {
      return true;
    }
  });

  const dragStart = (e: any, type: keyof typeof dragImage) => {
    const container = document.createElement('div');
    container.id = 'dragimage-container';
    const img = dragImage[type].replaceAll('color', 'DC6803');
    Object.assign(container.style, {
      opacity: '0.8',
      backgroundImage: `url("${img}")`,
      backgroundSize: '100% 100%',
      width: '95px',
      height: '30px'
    });

    document.body.appendChild(container);

    e.dataTransfer.setData(
      'data',
      JSON.stringify({
        bitmapFile: img,
        mode: AnnotationEditorType.STAMP,
        width: 94 * 0.7,
        height: 26 * 0.7,
        data: {
          name: currentUser.name,
          address: currentUser.address,
          email: currentUser.email,
          color: 'DC6803',
          type
        }
      })
    );
    e.dataTransfer.setDragImage(container, 0, 0);
  };

  const onItemClick = (e: any, type: keyof typeof dragImage) => {
    const container = document.createElement('div');
    container.id = 'dragimage-container';
    Object.assign(container.style, {
      opacity: '0.8',
      backgroundImage: `url("${dragImage.signature.replaceAll('color', 'DC6803')}")`,
      backgroundSize: '100% 100%',
      width: '94px',
      height: '26px',
      position: 'fixed',
      zIndex: '9999',
      transform: `translate3D(${e.clientX - 94}px,${e.clientY - 26}px,0)`,
      left: '0',
      top: '0'
    });
    props.onSignatureClick?.({
      type,
      name: currentUser.name,
      address: currentUser.address,
      email: currentUser.email,
      color: 'DC6803'
    });
    document.body.appendChild(container);
  };
  return (
    <div className="pl-10 pt-8 h-full relative flex flex-col pb-[100px] overflow-auto border-r border-gray-200">
      <div className="text-lg font-bold text-gray-900">{islinkSignContract ? 'Place Fields' : 'Pending Fields'}</div>
      <div className="mt-2  text-sm font-normal text-gray-500 mb-5 ">
        {islinkSignContract
          ? 'Place signatures at the last page of the document.'
          : 'Click on the field and jump to page'}
      </div>
      {islinkSignContract && !fields[0]?.completed ? (
        <div
          onClick={(e) => onItemClick(e, 'signature')}
          onDragStart={(e) => dragStart(e, 'signature')}
          onDragEnd={(e) => dragEnd(e)}
          draggable
          className="disabled pt-[22px] py-[14px] overflow-hidden relative cursor-pointer hover:bg-yellow-25 w-[128px] h-[88px] mb-4 mr-4 flex flex-col justify-center items-center border border-gray-200 rounded-lg"
        >
          <Edit02 color="#475467" size={20} />
          <span className="mt-[14px] text-xs text-gray-700 font-medium">Place Signature</span>
        </div>
      ) : (
        <ScrollArea className="flex-1 pr-8 mr-2 ">
          {Object.keys(fieldsGroup).map((key, i) => {
            const group = fieldsGroup[key];
            return (
              <div key={i} className="">
                {!islinkSignContract && <div className="text-xs font-normal text-gray-400">Page {key}</div>}
                {group.map((item, j) => {
                  const { type, completed, Id, linkId, required } = item;
                  const info = fieldsInfo[type as keyof typeof fieldsInfo];
                  const active = selectedField === Id;
                  return (
                    <div
                      key={j}
                      className={`${
                        active ? 'bg-primary-50 border-primary-600' : ''
                      } relative cursor-pointer mt-2 p-4 border border-gray-200 rounded-lg flex flex-row overflow-hidden`}
                      onClick={() => {
                        onFieldClick(linkId || Id);
                      }}
                    >
                      {required && (
                        <span className="bg-tangerine-50 absolute right-0 top-0 text-xxs px-[6px] text-primary-800 rounded-bl-lg">
                          Required
                        </span>
                      )}
                      {info?.icons[completed ? 'active' : 'normal']}
                      <div className="ml-4 text-sm">
                        <div className={`font-medium ${completed ? 'text-slime-500' : 'text-primary-800'} `}>
                          {info.text} Field
                        </div>
                        <div className="font-medium text-gray-500">{completed ? 'Completed' : 'Pending'}</div>
                      </div>
                      <div className="absolute right-4 top-7">
                        {completed ? <CheckGreenIcon /> : active ? <CircleCheckedIcon /> : <CircleIcon />}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </ScrollArea>
      )}
      <div className="absolute bottom-8 ">
        {!isCompleted && (
          <Tooltip
            content={
              <div className="text-xs font-normal text-gray-500 text-center">
                <div>Please complete all the pending</div>fields to finish the contract.
              </div>
            }
          >
            <div>
              <Button disabled={disabledButton} size="lg" className="w-[272px]" onClick={onSubmit}>
                Complete Contract
              </Button>
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
